import { useState, useCallback } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { PictureAsPdf, Print } from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { useAuth } from '../../auth/AuthProvider';

const GeneratePDFButtons = ({ order }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { company } = useAuth();
  const resolveOrderCode = useCallback((orderData) => (
    orderData?.public_code || orderData?.publicCode || orderData?.supplier_code || orderData?.supplierCode || orderData?.number || orderData?.id
  ), []);

  // Función mejorada para normalizar los datos de la orden
  const normalizeOrderData = useCallback((orderData) => {
    if (!orderData) return null;
    
    return {
      ...orderData,
      order_details: orderData.order_details?.map(detail => ({
        ...detail,
        product: {
          ...detail.product,
          description: detail.product?.description || 'Sin descripción',
          code: detail.product?.code || 'N/A'
        },
        order_bonus: detail.order_bonus?.map(bonus => ({
          ...bonus,
          product: {
            ...bonus.product,
            description: bonus.product?.description || 'Bonificación sin descripción'
          }
        })) || []
      })) || []
    };
  }, []);

  const generatePDF = useCallback(async () => {
    if (!order) return null;
    const { default: OrderPDF } = await import('./OrderPDF');
    const normalizedOrder = normalizeOrderData(order);
    return <OrderPDF order={normalizedOrder} company={company} />;
  }, [order, normalizeOrderData, company]);

  const handlePrint = async () => {
    if (!order || isPrinting) return;
    
    setIsPrinting(true);
    try {
      const pdfDoc = await generatePDF();
      if (!pdfDoc) throw new Error('No se pudo generar el documento');
      
      const blob = await pdf(pdfDoc).toBlob();
      const url = URL.createObjectURL(blob);
      
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 10000);
          setIsPrinting(false);
        }, 500);
      };
    } catch (error) {
      console.error('Error al imprimir:', error);
      setIsPrinting(false);
      alert(`Error al generar el PDF: ${error.message}`);
    }
  };

  const handleDownload = async () => {
    if (!order || isDownloading) return;
    
    setIsDownloading(true);
    try {
      const pdfDoc = await generatePDF();
      if (!pdfDoc) throw new Error('No se pudo generar el documento');
      
      const blob = await pdf(pdfDoc).toBlob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      const code = resolveOrderCode(order);
      const safeCode = code ? String(code).replace(/[^a-z0-9-_]/gi, '') : Date.now();
      link.download = `orden_${safeCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsDownloading(false);
      }, 200);
    } catch (error) {
      console.error('Error al descargar:', error);
      setIsDownloading(false);
      alert(`Error al descargar el PDF: ${error.message}`);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
      <Button
        variant="contained"
        color="secondary"
        startIcon={isPrinting ? <CircularProgress size={20} /> : <Print />}
        onClick={handlePrint}
        disabled={!order || isPrinting}
        sx={{ minWidth: '120px' }}
      >
        {isPrinting ? 'Preparando...' : 'Imprimir'}
      </Button>

      <Button
        variant="contained"
        color="primary"
        startIcon={isDownloading ? <CircularProgress size={20} /> : <PictureAsPdf />}
        onClick={handleDownload}
        disabled={!order || isDownloading}
        sx={{ minWidth: '120px' }}
      >
        {isDownloading ? 'Generando...' : 'Descargar'}
      </Button>
    </div>
  );
};

export default GeneratePDFButtons;
